import React, { useState } from "react";
import { Users, HeartPulse, Zap, Flame, Moon, ArrowRight, TrendingUp, Grid, BarChart3, Activity } from "lucide-react";
import { calcAgeGroupRisk } from "./helpers";
import WidgetInfoTooltip from "./WidgetInfoTooltip";

const METRICS_OPTIONS = [
  { id: "htaRate", label: "Tensión (PAS ≥ 130)", icon: HeartPulse, color: "#EF4444", bg: "#FEF2F2" },
  { id: "glucoseRate", label: "Glicemia (≥ 100)", icon: Activity, color: "#F97316", bg: "#FFF7ED" },
  { id: "whtrRate", label: "Grasa Visceral (WHtR > 0.50)", icon: Flame, color: "#F59E0B", bg: "#FFFBEB" },
  { id: "lipidRate", label: "Dislipidemia (Col ≥ 200)", icon: Activity, color: "#EAB308", bg: "#FEFCE8" },
  { id: "stressRate", label: "Tensión Autonómica / Estrés", icon: Zap, color: "#8B5CF6", bg: "#F5F3FF" },
  { id: "sedentaryRate", label: "Sedentarismo", icon: Activity, color: "#64748B", bg: "#F8FAFC" },
  { id: "sleepDeficitRate", label: "Déficit de Sueño (< 7h)", icon: Moon, color: "#3B82F6", bg: "#EFF6FF" },
];

const AGE_METRIC_INFO = {
  htaRate: {
    title: "Tensión Arterial Elevada / HTA",
    what: "Porcentaje de colaboradores de la cohorte con cifras de tensión arterial en rango de hipertensión.",
    calculation: "PAS ≥ 130 mmHg o PAD ≥ 80 mmHg.",
    standard: "ACC/AHA 2017 High Blood Pressure Clinical Practice Guideline.",
    relevance: "Evalúa cómo se incrementa la vulnerabilidad vascular conforme avanza la edad biológica.",
  },
  glucoseRate: {
    title: "Glicemia Basal Alterada",
    what: "Proporción de colaboradores del grupo etario con glucemia en ayunas en rango de prediabetes o diabetes.",
    calculation: "Glucosa sérica o capilar en ayunas ≥ 100 mg/dL.",
    standard: "ADA Standards of Care in Diabetes 2024.",
    relevance: "Detección temprana de resistencia a la insulina para prevenir complicaciones microvasculares.",
  },
  whtrRate: {
    title: "Grasa Visceral (WHtR > 0.50)",
    what: "Prevalencia de adiposidad visceral central profunda por cohorte generacional.",
    calculation: "Relación Cintura-Estatura (WHtR) > 0.50.",
    standard: "Consenso IDF & Margaret Ashwell 2012.",
    relevance: "Predictor universal de riesgo cardiometabólico independiente de la pérdida de estatura o masa magra.",
  },
  lipidRate: {
    title: "Dislipidemia Aterogénica",
    what: "Prevalencia de niveles anormales de lípidos plasmáticos en la cohorte.",
    calculation: "Colesterol total ≥ 200 mg/dL o Triglicéridos ≥ 150 mg/dL.",
    standard: "National Cholesterol Education Program (NCEP ATP III).",
    relevance: "Marcador de riesgo aterosclerótico coronario y cerebrovascular acumulativo con la edad.",
  },
  stressRate: {
    title: "Tensión Autonómica / Estrés",
    what: "Porcentaje de la cohorte con sobrecarga neurovegetativa o estrés biológico elevado.",
    calculation: "Índice de Tensión de Baevsky > 400 pts (rPPG FaceScan).",
    standard: "Baevsky RM Stress Index & Task Force ESC/NASPE 1996.",
    relevance: "Identifica si el estrés laboral impacta desproporcionadamente a jóvenes o a mandos medios/seniors.",
  },
  sedentaryRate: {
    title: "Sedentarismo Ocupacional",
    what: "Proporción de colaboradores con inactividad física o permanencia prolongada sentados.",
    calculation: "< 600 MET-minutos/semana en IPAQ o > 8 horas al día continuas sentados.",
    standard: "OMS Directrices Mundiales sobre Actividad Física y Sedentarismo 2020.",
    relevance: "Determina la pérdida precoz de capacidad cardiopulmonar y atrofia muscular por generación.",
  },
  sleepDeficitRate: {
    title: "Déficit de Sueño (< 7h)",
    what: "Prevalencia de deuda o restricción biológica de sueño por cohorte.",
    calculation: "Promedio de sueño reportado < 7.0 horas continuas por noche.",
    standard: "National Sleep Foundation (NSF) & American Academy of Sleep Medicine (AASM).",
    relevance: "Factor crítico en fatiga laboral, micro-sueños en puestos de riesgo y declive cognitivo diurno.",
  },
};

export default function AgeGroupRiskCard({ employees = [], forceSimulated = false }) {
  const data = calcAgeGroupRisk(employees, forceSimulated);
  const [viewMode, setViewMode] = useState("matrix"); // 'matrix' | 'bars'
  const [activeMetricId, setActiveMetricId] = useState("htaRate");

  const currentMetric = METRICS_OPTIONS.find((m) => m.id === activeMetricId) || METRICS_OPTIONS[0];
  const activeMetricDetails = AGE_METRIC_INFO[activeMetricId] || AGE_METRIC_INFO.htaRate;

  // Identificar cohorte con mayor impacto en la métrica activa
  const highestCohort = [...data.cohorts].sort((a, b) => (b[activeMetricId] || 0) - (a[activeMetricId] || 0))[0];

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm relative"
      data-testid="age-group-risk-card"
    >
      {/* Cabecera */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
              <Users size={12} />
              Segmentación Generacional
            </span>
            <span className="text-xs text-slate-400">
              4 cohortes etarias analizadas
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <h3 className="text-base font-bold text-slate-800">
              Distribución de Patologías por Rango de Edad
            </h3>
            <WidgetInfoTooltip
              title="Segmentación Generacional de Factores de Riesgo"
              align="left"
              what="Comparativa epidemiológica del perfil de salud segmentado en 4 cohortes etarias (<28, 28-39, 40-49 y ≥50 años)."
              calculation="Cálculo de la tasa de prevalencia porcentual de cada patología clínica dentro de cada grupo etario a partir de la fecha de nacimiento y somatometría en F1."
              standard="Consensos ACC/AHA 2017, ADA 2024, IDF/Ashwell 2012, NCEP ATP III, Baevsky RM y OMS IPAQ 2020."
              relevance="Permite personalizar los planes de salud corporativa: enfocar prevención ergonómica y manejo de estrés en jóvenes, y control cardiovascular y metabólico en colaboradores seniors."
            />
          </div>
          <p className="text-xs text-slate-500">
            Comparativa de factores de riesgo entre generaciones de colaboradores de la plantilla.
          </p>
        </div>

        {/* Toggle de Vista: Matriz Consolidada vs Barras Individuales */}
        <div className="flex items-center gap-2">
          {highestCohort && viewMode === "bars" && (
            <div className="hidden sm:flex items-center gap-2 bg-slate-50 border border-slate-200/70 rounded-xl px-3 py-1.5">
              <TrendingUp size={14} className="text-amber-600" />
              <span className="text-xs text-slate-600">
                Foco prioritario: <strong>{highestCohort.name}</strong> ({highestCohort[activeMetricId]}%)
              </span>
            </div>
          )}

          <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200/60 self-start lg:self-auto">
            <button
              type="button"
              data-testid="toggle-matrix-view"
              onClick={() => setViewMode("matrix")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === "matrix"
                  ? "bg-white text-slate-800 shadow-xs border border-slate-200/50"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Grid size={13} />
              <span>Matriz Consolidada</span>
            </button>
            <button
              type="button"
              data-testid="toggle-chart-view"
              onClick={() => setViewMode("bars")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === "bars"
                  ? "bg-white text-slate-800 shadow-xs border border-slate-200/50"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <BarChart3 size={13} />
              <span>Por Métrica</span>
            </button>
          </div>
        </div>
      </div>

      {/* VISTA 1: MATRIZ CONSOLIDADA MULTI-FACTOR (UN SOLO VISTAZO) */}
      {viewMode === "matrix" && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70 text-slate-500 font-semibold">
                <th className="py-2.5 px-3">Cohorte Etaria</th>
                <th className="py-2.5 px-2 text-center">Colab.</th>
                <th className="py-2.5 px-2 text-center text-rose-700">
                  <div className="inline-flex items-center justify-center gap-1">
                    <span>Tensión (HTA)</span>
                    <WidgetInfoTooltip
                      title={AGE_METRIC_INFO.htaRate.title}
                      size="sm"
                      align="left"
                      what={AGE_METRIC_INFO.htaRate.what}
                      calculation={AGE_METRIC_INFO.htaRate.calculation}
                      standard={AGE_METRIC_INFO.htaRate.standard}
                      relevance={AGE_METRIC_INFO.htaRate.relevance}
                    />
                  </div>
                </th>
                <th className="py-2.5 px-2 text-center text-orange-700">
                  <div className="inline-flex items-center justify-center gap-1">
                    <span>Glicemia</span>
                    <WidgetInfoTooltip
                      title={AGE_METRIC_INFO.glucoseRate.title}
                      size="sm"
                      align="left"
                      what={AGE_METRIC_INFO.glucoseRate.what}
                      calculation={AGE_METRIC_INFO.glucoseRate.calculation}
                      standard={AGE_METRIC_INFO.glucoseRate.standard}
                      relevance={AGE_METRIC_INFO.glucoseRate.relevance}
                    />
                  </div>
                </th>
                <th className="py-2.5 px-2 text-center text-amber-700">
                  <div className="inline-flex items-center justify-center gap-1">
                    <span>Grasa Visc.</span>
                    <WidgetInfoTooltip
                      title={AGE_METRIC_INFO.whtrRate.title}
                      size="sm"
                      align="left"
                      what={AGE_METRIC_INFO.whtrRate.what}
                      calculation={AGE_METRIC_INFO.whtrRate.calculation}
                      standard={AGE_METRIC_INFO.whtrRate.standard}
                      relevance={AGE_METRIC_INFO.whtrRate.relevance}
                    />
                  </div>
                </th>
                <th className="py-2.5 px-2 text-center text-yellow-700">
                  <div className="inline-flex items-center justify-center gap-1">
                    <span>Lípidos</span>
                    <WidgetInfoTooltip
                      title={AGE_METRIC_INFO.lipidRate.title}
                      size="sm"
                      align="left"
                      what={AGE_METRIC_INFO.lipidRate.what}
                      calculation={AGE_METRIC_INFO.lipidRate.calculation}
                      standard={AGE_METRIC_INFO.lipidRate.standard}
                      relevance={AGE_METRIC_INFO.lipidRate.relevance}
                    />
                  </div>
                </th>
                <th className="py-2.5 px-2 text-center text-purple-700">
                  <div className="inline-flex items-center justify-center gap-1">
                    <span>Estrés</span>
                    <WidgetInfoTooltip
                      title={AGE_METRIC_INFO.stressRate.title}
                      size="sm"
                      align="left"
                      what={AGE_METRIC_INFO.stressRate.what}
                      calculation={AGE_METRIC_INFO.stressRate.calculation}
                      standard={AGE_METRIC_INFO.stressRate.standard}
                      relevance={AGE_METRIC_INFO.stressRate.relevance}
                    />
                  </div>
                </th>
                <th className="py-2.5 px-2 text-center text-slate-700">
                  <div className="inline-flex items-center justify-center gap-1">
                    <span>Sedentarismo</span>
                    <WidgetInfoTooltip
                      title={AGE_METRIC_INFO.sedentaryRate.title}
                      size="sm"
                      align="left"
                      what={AGE_METRIC_INFO.sedentaryRate.what}
                      calculation={AGE_METRIC_INFO.sedentaryRate.calculation}
                      standard={AGE_METRIC_INFO.sedentaryRate.standard}
                      relevance={AGE_METRIC_INFO.sedentaryRate.relevance}
                    />
                  </div>
                </th>
                <th className="py-2.5 px-2 text-center text-blue-700">
                  <div className="inline-flex items-center justify-center gap-1">
                    <span>Sueño &lt;7h</span>
                    <WidgetInfoTooltip
                      title={AGE_METRIC_INFO.sleepDeficitRate.title}
                      size="sm"
                      align="right"
                      what={AGE_METRIC_INFO.sleepDeficitRate.what}
                      calculation={AGE_METRIC_INFO.sleepDeficitRate.calculation}
                      standard={AGE_METRIC_INFO.sleepDeficitRate.standard}
                      relevance={AGE_METRIC_INFO.sleepDeficitRate.relevance}
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.cohorts.map((cohort) => {
                const getHeatBg = (val) => {
                  if (val >= 35) return "bg-rose-50 text-rose-700 font-bold border border-rose-200/60";
                  if (val >= 20) return "bg-amber-50 text-amber-700 font-semibold border border-amber-200/60";
                  return "bg-slate-50 text-slate-700 font-medium";
                };

                return (
                  <tr key={cohort.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 px-3">
                      <div className="font-bold text-slate-800">{cohort.name}</div>
                      <div className="text-[11px] text-slate-400">{cohort.role}</div>
                    </td>
                    <td className="py-3 px-2 text-center text-slate-500 font-medium">
                      {cohort.count} <span className="text-[10px] text-slate-400">({cohort.populationPercent}%)</span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] ${getHeatBg(cohort.htaRate)}`}>
                        {cohort.htaRate}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] ${getHeatBg(cohort.glucoseRate)}`}>
                        {cohort.glucoseRate}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] ${getHeatBg(cohort.whtrRate)}`}>
                        {cohort.whtrRate}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] ${getHeatBg(cohort.lipidRate)}`}>
                        {cohort.lipidRate}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] ${getHeatBg(cohort.stressRate)}`}>
                        {cohort.stressRate}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] ${getHeatBg(cohort.sedentaryRate)}`}>
                        {cohort.sedentaryRate}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] ${getHeatBg(cohort.sleepDeficitRate)}`}>
                        {cohort.sleepDeficitRate}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* VISTA 2: BARRAS COMPARATIVAS INDIVIDUALES */}
      {viewMode === "bars" && (
        <div>
          {/* Selector de Métrica */}
          <div className="flex flex-wrap gap-2 mb-4">
            {METRICS_OPTIONS.map((metric) => {
              const Icon = metric.icon;
              const isActive = activeMetricId === metric.id;
              return (
                <button
                  key={metric.id}
                  type="button"
                  onClick={() => setActiveMetricId(metric.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                    isActive
                      ? "bg-slate-800 text-white border-slate-800 shadow-sm"
                      : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  <Icon size={13} style={{ color: isActive ? "#FFFFFF" : metric.color }} />
                  {metric.label}
                </button>
              );
            })}
          </div>

          {/* Banner de Métrica Activa con Tooltip y Estándar */}
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200/70 rounded-xl px-3.5 py-2 mb-5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-800">{activeMetricDetails.title}</span>
              <WidgetInfoTooltip
                title={activeMetricDetails.title}
                size="sm"
                align="left"
                what={activeMetricDetails.what}
                calculation={activeMetricDetails.calculation}
                standard={activeMetricDetails.standard}
                relevance={activeMetricDetails.relevance}
              />
            </div>
            <span className="text-[11px] text-slate-500 font-medium hidden sm:inline">
              Criterio clínico: <strong className="text-slate-700">{activeMetricDetails.calculation}</strong>
            </span>
          </div>

          {/* Gráfico de Barras Comparativo por Generación */}
          <div className="space-y-4">
            {data.cohorts.map((cohort) => {
              const val = cohort[activeMetricId] || 0;
              return (
                <div key={cohort.id} className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/50">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-900">
                        {cohort.name}
                      </span>
                      <span className="text-[11px] text-slate-500">
                        ({cohort.role})
                      </span>
                      <span className="text-[10px] font-medium text-slate-400 ml-1">
                        • {cohort.count} personas ({cohort.populationPercent}% plantilla)
                      </span>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      <span className="text-xs font-extrabold text-slate-800">
                        {val}% afectados
                      </span>
                    </div>
                  </div>

                  {/* Barra de Porcentaje */}
                  <div className="w-full bg-slate-200/80 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="h-2.5 rounded-full transition-all duration-700 shadow-sm"
                      style={{
                        width: `${Math.max(4, val)}%`,
                        backgroundColor: currentMetric.color,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
